/* Syzygy brew calculator. Data: brew-recipes.v2.json (schema 2, approved profiles only).
   Typed amounts (v3): start from water or coffee. Steps come from the nearest approved profile by water.
   Rounding is half up: coffee to 0.1 g, water and pour targets to whole grams. Pour targets are the approved
   cumulative targets scaled to the amount, so the last pour always equals the total water.
   All rounding uses exact integer math (F11, v3c): coffee is held in tenths of a gram, recipe fractions such as
   0.4167 are read from their decimal digits, and n/d is rounded half up as floor((2n + d) / 2d) with whole numbers,
   so no float product like 16.9 x 15 = 253.49999999999997 can round the wrong way. */
(function () {
  'use strict';
  /* Pure engine (also loaded by tools/sweep_engine.js under Node for the validator sweep). */
  var E = {};
  /* Exact helpers. Every argument is a whole number at or above 0 (d above 0), far below 2^53. */
  E.idiv = function (a, b) { return (a - a % b) / b; };
  E.half = function (n, d) { return E.idiv(2 * n + d, 2 * d); };
  E.frac = function (v) {
    var m = /^(\d+)(?:\.(\d+))?$/.exec(String(v));
    if (!m) throw new Error('Unsupported number ' + v);
    var f = m[2] || '';
    return { n: Number(m[1] + f), d: Math.pow(10, f.length) };
  };
  E.t1 = function (t) { return E.idiv(t, 10) + '.' + (t % 10); };
  E.coffee10 = function (water, ratio) { return E.half(10 * water, ratio); };
  E.mmss = function (s) { var m = Math.floor(s / 60), r = s % 60; return m + ':' + (r < 10 ? '0' : '') + r; };
  E.parse = function (text, mode) {
    var t = String(text == null ? '' : text).trim().replace(',', '.');
    if (!t) return null;
    if (!/^\d{1,5}(\.\d+)?$/.test(t)) return NaN;
    /* Half up straight from the typed digits: the first dropped digit decides. */
    var parts = t.split('.'), ip = Number(parts[0]), fp = (parts[1] || '') + '00';
    if (mode === 'coffee') return (ip * 10 + Number(fp.charAt(0)) + (Number(fp.charAt(1)) >= 5 ? 1 : 0)) / 10;
    return ip + (Number(fp.charAt(0)) >= 5 ? 1 : 0);
  };
  E.limits = function (b, profiles) {
    var ws = profiles.filter(function (p) { return p.brewerId === b.id; }).map(function (p) { return p.waterG; }).sort(function (a, c) { return a - c; });
    return { minW: b.minBrewWaterG || ws[0], maxW: b.maxBrewWaterG, minD: b.minDoseG || 0, maxD: b.maxDoseG };
  };
  /* dose is an exact fraction {n, d}: typed coffee in tenths (n/10) or water / ratio. */
  E.resolve = function (lim, mode, amount, ratio) {
    var w, dose, c10;
    if (mode === 'coffee') { c10 = Math.round(amount * 10); w = E.idiv(c10 * ratio + 5, 10); dose = { n: c10, d: 10 }; }
    else { w = amount; dose = { n: w, d: ratio }; c10 = E.coffee10(w, ratio); }
    var minD10 = Math.round(lim.minD * 10), maxD10 = Math.round(lim.maxD * 10);
    if (w < lim.minW || w > lim.maxW || dose.n * 10 > maxD10 * dose.d || dose.n * 10 < minD10 * dose.d) return null;
    return { water: w, dose: dose, coffee: E.t1(c10) };
  };
  E.allowed = function (lim, mode, amount, ratios) { return ratios.filter(function (r) { return !!E.resolve(lim, mode, amount, r); }); };
  E.coffeeRange = function (lim, ratios) {
    var lo = null, hi = null;
    for (var i = 10; i <= Math.round(lim.maxD * 10); i++) {
      var c = i / 10;
      if (E.allowed(lim, 'coffee', c, ratios).length) { if (lo === null) lo = c; hi = c; }
    }
    return { lo: lo, hi: hi };
  };
  E.base = function (profiles, brewerId, water) {
    return profiles.filter(function (p) { return p.brewerId === brewerId; }).sort(function (a, b) {
      return (Math.abs(a.waterG - water) - Math.abs(b.waterG - water)) || (a.waterG - b.waterG);
    })[0];
  };
  /* Join a number and its unit with a no break space so "141 g" never wraps as "141" then "g". */
  E.nb = function (t) { return String(t).replace(/(\d) g\b/g, '$1\u00a0g'); };
  E.fmt = function (t, o) { return String(t == null ? '' : t).replace(/\{(\w+)\}/g, function (m, k) { return o && o[k] != null ? o[k] : m; }); };
  E.compute = function (p, water, dose, coffee) {
    var prev = 0, ok = true, tc = E.frac(p.tempC);
    var vars = { coffee_g: coffee, water_g: String(water), temp_c: String(E.half(tc.n, tc.d)), temp_f: String(E.half(9 * tc.n + 160 * tc.d, 5 * tc.d)), min_time: E.mmss(p.timing.minSeconds), max_time: E.mmss(p.timing.maxSeconds) };
    var steps = p.steps.map(function (s) {
      var o = { type: s.type, add: null, cum: null }, v = vars;
      if (s.type === 'pour') {
        var t = s.target || {}, fr = E.frac(t.value);
        var cum = t.kind === 'water_fraction' ? E.half(water * fr.n, fr.d) : E.half(dose.n * fr.n, dose.d * fr.d);
        if (!(cum > prev) || cum > water) ok = false;
        o.cum = cum; o.add = cum - prev; prev = cum;
        v = Object.assign({}, vars, { target_g: String(cum) });
      }
      o.text = E.fmt(s.text, v);
      return o;
    });
    if (prev !== water) ok = false;
    return { ok: ok, water: water, coffee: coffee, tempC: vars.temp_c, tempF: vars.temp_f, steps: steps };
  };
  /* Brew timer plan. One cue per step (setup steps share the ready cue, the French press plunge cue also covers serving).
     Times come from the approved data: "At 0:30" in Kalita pours, waitSeconds on the waits between pours (Chemex 45 s, French press
     bloom 30 s; the wording is the fallback), timing.minSeconds for the steep and the end of the brew. A wait between pours counts
     from the start of the previous pour (CoS decision), so Chemex pours land at 0:00, 0:45, 1:30 and 2:15, the times its steps name.
     Prompt keys are an allowlist; the strings live in the section's timer.prompts and timer.headsUp with numeric slots only. */
  E.POUR_S = 10; E.HEADS_UP_S = 5; E.DONE_AFTER_PLUNGE_S = 20;
  E.PROMPTS = ['ready', 'bloom', 'bloomStir', 'bloomWait', 'pour2', 'pour3', 'pour4', 'final', 'between', 'drain', 'steep', 'plunge', 'serve_chemex', 'serve_kalita-wave', 'done'];
  E.HEADS_UP = ['pour2', 'pour3', 'pour4', 'final', 'plunge', 'serve_chemex', 'serve_kalita-wave'];
  E.timerPlan = function (p, c) {
    var src = p.steps, cues = [], f = -1, n = 0, i;
    for (i = 0; i < src.length; i++) if (src[i].type === 'pour') { if (f < 0) f = i; n++; }
    if (f < 0 || n > 5) return null;
    var ready = []; for (i = 0; i < f; i++) ready.push(i);
    cues.push({ key: 'ready', at: null, steps: ready, n: f });
    var k = 0, lastPour = 0, wait = 0;
    for (i = f; i < src.length; i++) {
      var st = src[i], t = st.type, last = cues[cues.length - 1], m, key, at;
      if (t === 'pour') {
        k++;
        m = /^(?:At|A los|Al) (\d+):(\d\d)\b/.exec(st.text);
        at = m ? Number(m[1]) * 60 + Number(m[2]) : k === 1 ? 0 : lastPour + wait;
        key = k === 1 ? (/\b(?:stir|revuelve)\b/i.test(st.text) ? 'bloomStir' : 'bloom') : k === n ? 'final' : 'pour' + k;
        cues.push({ key: key, at: at, steps: [i], g: c.steps[i].cum, headsUp: E.HEADS_UP.indexOf(key) > -1 ? key : null });
        lastPour = at; wait = 0;
      } else if (t === 'wait') {
        if (k < n) { m = /(\d+) (?:seconds|segundos)/.exec(st.text); wait = typeof st.waitSeconds === 'number' ? st.waitSeconds : m ? Number(m[1]) : 0; key = k === 1 ? 'bloomWait' : 'between'; }
        else key = p.timing.kind === 'steep' ? 'steep' : 'drain';
        cues.push({ key: key, at: lastPour + E.POUR_S, steps: [i], headsUp: null });
      } else if (t === 'press' && last.key === 'steep') {
        var cover = [i]; if (src[i + 1] && src[i + 1].type === 'serve') { cover.push(i + 1); i++; }
        at = last.at + p.timing.minSeconds;
        cues.push({ key: 'plunge', at: at, steps: cover, headsUp: 'plunge' });
        cues.push({ key: 'done', at: at + E.DONE_AFTER_PLUNGE_S, steps: [], headsUp: null });
      } else if (t === 'serve' && E.PROMPTS.indexOf('serve_' + p.methodId) > -1) {
        key = 'serve_' + p.methodId;
        cues.push({ key: key, at: Math.max(p.timing.minSeconds, last.at + 30), steps: [i], headsUp: key });
      } else return null;
    }
    return cues;
  };
  if (typeof module === 'object' && module.exports) { module.exports = E; return; }

  var root = document.querySelector('[data-bc-root]');
  if (!root || root.getAttribute('data-bc-init')) return;
  root.setAttribute('data-bc-init', '1');
  var S = {};
  try { S = JSON.parse(root.querySelector('[data-bc-strings]').textContent); } catch (e) { S = {}; }
  var ALIAS = { 'kalita-wave': 'kalita-wave', kalita: 'kalita-wave', chemex: 'chemex', 'french-press': 'french-press', frenchpress: 'french-press', press: 'french-press' };
  var SOURCES = ['guide', 'gear', 'home', 'nav', 'video', 'product', 'email', 'social'];
  var $ = function (sel, el) { return (el || root).querySelector(sel); };
  var $$ = function (sel, el) { return Array.prototype.slice.call((el || root).querySelectorAll(sel)); };
  var fmt = E.fmt;
  var form = $('[data-bc-form]'), controls = $('[data-bc-controls]'), statusEl = $('[data-bc-status]');
  var resultEl = $('[data-bc-result]'), placeholder = $('[data-bc-placeholder]'), errorEl = $('[data-bc-error]'), loadingEl = $('[data-bc-loading]');
  var cfg = null, M = {}, B = {}, P = [], RATIO = { def: 16, allowed: [14, 15, 16, 17, 18, 19] };
  var st = { method: null, brewer: null, mode: 'water', text: '', amount: null, problem: 'empty', allowed: null, calc: null, ratio: null, shown: false, coffee: {}, source: null };
  var sent = {}, started = false, typeTimer = null, genTimer = null, ratioKey = '';
  var query = new URLSearchParams(window.location.search);
  var entry = (function () {
    var s = (query.get('src') || '').toLowerCase();
    if (SOURCES.indexOf(s) > -1) return s;
    return (query.get('recipe') || query.get('water') || query.get('coffee')) ? 'recipe_link' : query.get('method') ? 'method_link' : 'direct';
  })();
  function esc(t) { return String(t == null ? '' : t).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function say(msg) { if (!statusEl) return; statusEl.textContent = ''; window.setTimeout(function () { statusEl.textContent = msg; }, 60); }
  function g(n) { return String(n).replace(/\.0$/, ''); }

  /* Analytics: dataLayer and Shopify.analytics.publish only, never customer text. */
  function track(name, data) {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function' && !cp.analyticsProcessingAllowed()) return;
    } catch (e) {}
    var ev = 'syzygy:' + name;
    var payload = Object.assign({ schema_version: cfg ? cfg.schemaVersion : null, config_version: cfg ? cfg.configVersion : null, default_ratio: RATIO.def, entry_source: entry }, data || {});
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: ev }, payload)); } catch (e) {}
    try { if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish(ev, payload); } catch (e) {}
  }
  function once(key, name, data) { if (sent[key]) return; sent[key] = 1; track(name, data); }
  function recipeData(extra) {
    var c = st.calc;
    return Object.assign({ method_id: st.method, brewer_id: st.brewer, recipe_id: c ? c.profile.id : null, recipe_version: c ? c.profile.version : null,
      water_g: c ? c.water : null, ratio: st.ratio, is_default_ratio: st.ratio === defaultRatio(), mode: st.mode, amount_g: c ? st.amount : null }, extra || {});
  }

  /* Data */
  function defaultRatio() { var m = M[st.method]; return (m && m.ratio && m.ratio.default) || RATIO.def; }
  function lim(bid) { return E.limits(B[bid], P); }
  function load(c) {
    if (!c || c.schemaVersion !== 2 || !Array.isArray(c.methods) || !Array.isArray(c.brewers) || !Array.isArray(c.profiles)) throw new Error('Unsupported recipe data');
    cfg = c; M = {}; B = {};
    if (c.ratio && c.ratio.default && Array.isArray(c.ratio.allowed)) RATIO = { def: c.ratio.default, allowed: c.ratio.allowed };
    c.methods.forEach(function (m) { M[m.id] = m; });
    c.brewers.forEach(function (b) { if (M[b.methodId] && b.maxBrewWaterG > 0 && b.maxDoseG > 0) B[b.id] = b; });
    P = c.profiles.filter(function (p) {
      var b = B[p.brewerId];
      return p.status === 'approved' && p.approvedBy && p.approvedAt && M[p.methodId] && b && b.methodId === p.methodId &&
        p.waterG > 0 && p.waterG <= b.maxBrewWaterG && Array.isArray(p.steps) && p.timing &&
        E.compute(p, p.waterG, { n: p.waterG, d: RATIO.def }, E.t1(E.coffee10(p.waterG, RATIO.def))).ok;
    });
    if (!P.length) throw new Error('No approved recipes');
  }
  function profilesFor(bid) { return P.filter(function (p) { return p.brewerId === bid; }).sort(function (a, b) { return a.waterG - b.waterG; }); }
  function brewersFor(mid) { return Object.keys(B).map(function (k) { return B[k]; }).filter(function (b) { return b.methodId === mid && profilesFor(b.id).length; }); }
  function blurbFor(mid, r) {
    var bl = cfg.blurbs || {}, band = null;
    (bl.bands || []).forEach(function (x) { if (x.ratios.indexOf(r) > -1) band = x.id; });
    var it = (bl.items || []).filter(function (i) { return i.methodId === mid && i.band === band && i.status === 'approved'; })[0];
    if (!it && bl.fallback && bl.fallback.status === 'approved') it = bl.fallback;
    var edge = (bl.edgeNotes || []).filter(function (n) { return n.ratio === r && n.status === 'approved'; })[0] || null;
    return { blurb: it || null, edge: edge, taste: bl.tasteNote || '' };
  }
  /* Evaluate the typed amount: sets st.amount, st.problem ('empty', 'nan', 'low', 'high' or null), st.allowed, st.calc. */
  function evaluate() {
    st.evalKey = st.mode + '|' + st.text;
    st.calc = null; st.allowed = null; st.problem = null; st.nearest = null;
    if (!st.brewer) { st.problem = 'empty'; return; }
    var L = lim(st.brewer), v = E.parse(st.text, st.mode);
    st.amount = v;
    if (v === null) { st.problem = 'empty'; return; }
    if (isNaN(v)) { st.problem = 'nan'; return; }
    var list = E.allowed(L, st.mode, v, RATIO.allowed);
    if (!list.length) {
      var lo = L.minW, hi = L.maxW;
      if (st.mode === 'coffee') { var cr = E.coffeeRange(L, RATIO.allowed); lo = cr.lo; hi = cr.hi; }
      st.problem = v < lo ? 'low' : 'high'; st.nearest = v < lo ? lo : hi;
      return;
    }
    st.allowed = list;
    if (list.indexOf(st.ratio) < 0) {
      var def = defaultRatio();
      st.ratio = list.indexOf(def) > -1 ? def : list.slice().sort(function (a, b) { return Math.abs(a - def) - Math.abs(b - def) || a - b; })[0];
    }
    var res = E.resolve(L, st.mode, v, st.ratio), p = E.base(P, st.brewer, res.water);
    var c = E.compute(p, res.water, res.dose, res.coffee);
    c.profile = p; st.calc = c;
  }

  /* UI */
  function setActive(mid) { if (mid) root.setAttribute('data-active-method', mid); else root.removeAttribute('data-active-method'); }
  function setParams(o) {
    try {
      var u = new URL(window.location.href);
      Object.keys(o).forEach(function (k) { if (o[k] == null) u.searchParams.delete(k); else u.searchParams.set(k, o[k]); });
      window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
    } catch (e) {}
  }
  function notice(msg) {
    var n = $('[data-bc-notice]');
    if (!n) { n = document.createElement('p'); n.className = 'bc__notice'; n.setAttribute('data-bc-notice', ''); form.insertBefore(n, form.firstChild); }
    n.textContent = msg; n.hidden = !msg;
  }
  function radio(name, value, label, checked) {
    return '<label class="bc-chip"><input type="radio" name="' + name + '" value="' + esc(value) + '"' + (checked ? ' checked' : '') + '><span class="bc-chip__text">' + label + '</span></label>';
  }
  function renderControls() {
    var mid = st.method, m = M[mid], brs = brewersFor(mid), h = '';
    if (brs.length === 1) st.brewer = brs[0].id;
    h += '<fieldset class="bc-field"><legend class="bc-field__legend"><span class="bc-field__num" aria-hidden="true">2</span>' + esc(S.brewerLegend) + '</legend><div class="bc-chips">';
    brs.forEach(function (b) { h += radio('bc-brewer', b.id, esc(b.label), st.brewer === b.id); });
    h += '</div><p class="bc-field__help"><a href="' + esc(m.guideUrl) + '" data-bc-guide-link data-method="' + esc(mid) + '" data-link-type="brewer_unlisted">' + esc(S.brewerUnlisted) + '</a></p></fieldset>';
    h += '<div data-bc-batch></div><div data-bc-ratio></div>';
    h += '<div class="bc-submit"><button type="submit" class="btn btn-solid btn-lg bc-btn">' + esc(S.submit) + '</button><p class="bc-error" data-bc-form-error hidden></p></div>';
    controls.innerHTML = h; ratioKey = '';
    renderBatch(); renderRatio();
  }
  function renderBatch() {
    var box = $('[data-bc-batch]'); if (!box) return;
    if (!st.brewer) { box.innerHTML = ''; return; }
    var h = '<fieldset class="bc-field bc-field--amount"><legend class="bc-field__legend"><span class="bc-field__num" aria-hidden="true">3</span>' + esc(S.batchLegend) + '</legend>';
    h += '<fieldset class="bc-mode"><legend class="bc-mode__legend">' + esc(S.modeLegend) + '</legend><div class="bc-chips">' +
      radio('bc-mode', 'water', esc(S.modeWater), st.mode === 'water') + radio('bc-mode', 'coffee', esc(S.modeCoffee), st.mode === 'coffee') + '</div></fieldset>';
    h += '<label class="bc-amount__label" for="bc-amount" data-bc-amount-label></label>' +
      '<div class="bc-amount"><input id="bc-amount" class="bc-amount__input" type="text" inputmode="decimal" autocomplete="off" spellcheck="false" enterkeyhint="go" aria-describedby="bc-amount-msg bc-amount-help" data-bc-amount value="' + esc(st.text) + '"><span class="bc-amount__unit" aria-hidden="true">g</span></div>' +
      '<div class="bc-amount__msg" id="bc-amount-msg" data-bc-amount-msg></div>';
    h += '<p class="bc-quick__label" id="bc-quick-label">' + esc(S.quickLabel) + '</p><div class="bc-quick" role="group" aria-labelledby="bc-quick-label" data-bc-quick></div>';
    h += '<p class="bc-field__help" id="bc-amount-help">' + esc(S.batchHelp) + '</p></fieldset>';
    box.innerHTML = h;
    updateAmountUi(true);
  }
  function quickValue(p) { return st.mode === 'coffee' ? g(E.t1(E.coffee10(p.waterG, RATIO.def))) : String(p.waterG); }
  function updateAmountUi(showProblem) {
    var lab = $('[data-bc-amount-label]'), q = $('[data-bc-quick]'), msg = $('[data-bc-amount-msg]');
    if (!lab) return;
    lab.textContent = st.mode === 'coffee' ? S.amountLabelCoffee : S.amountLabelWater;
    var help = $('#bc-amount-help'); if (help) help.textContent = (st.mode === 'coffee' && S.batchHelpCoffee) || S.batchHelp;
    var qk = st.brewer + '|' + st.mode;
    if (q.getAttribute('data-key') !== qk) {
      var h = '';
      profilesFor(st.brewer).forEach(function (p) {
        var v = quickValue(p);
        h += '<button type="button" class="bc-quick__btn" data-bc-quick-value="' + esc(v) + '" aria-pressed="false">' +
          esc(nb(fmt(st.mode === 'coffee' ? S.batchOptionCoffee : S.batchOption, { water: v, coffee: v }))) + '</button>';
      });
      q.innerHTML = h; q.setAttribute('data-key', qk);
    }
    $$('[data-bc-quick-value]', q).forEach(function (b) {
      b.setAttribute('aria-pressed', String(st.problem === null && g(st.amount) === b.getAttribute('data-bc-quick-value')));
    });
    var t = '';
    if (st.calc) t = esc(nb(st.mode === 'coffee' ? fmt(S.worksOutWater, { water: st.calc.water, ratio: st.ratio }) : fmt(S.worksOutCoffee, { coffee: st.calc.coffee, ratio: st.ratio })));
    else if (showProblem && st.problem && st.problem !== 'empty') t = problemHtml();
    msg.innerHTML = t;
    msg.classList.toggle('bc-amount__msg--problem', !st.calc && !!t);
  }
  function problemText() {
    var b = B[st.brewer], key = st.problem === 'nan' ? 'amountNaN' : (st.mode === 'coffee' ? 'coffee' : 'water') + (st.problem === 'low' ? 'Low' : 'High');
    var ex = quickValue(profilesFor(st.brewer)[0]);
    return fmt(S[key], { brewer: b.label, min: g(st.nearest), max: g(st.nearest), example: ex });
  }
  function problemHtml() {
    var h = '<span>' + esc(nb(problemText())) + '</span>';
    if (st.nearest != null) h += ' <button type="button" class="bc-amount__use" data-bc-use="' + esc(g(st.nearest)) + '">' + esc(nb(fmt(S.useNearest, { g: g(st.nearest) }))) + '</button>';
    return h;
  }
  function renderRatio() {
    var box = $('[data-bc-ratio]'); if (!box) return;
    var list = st.allowed || RATIO.allowed, bl = cfg.blurbs || {};
    if (list.indexOf(st.ratio) < 0) st.ratio = list.indexOf(defaultRatio()) > -1 ? defaultRatio() : list[0];
    var key = list.join(',') + '|' + st.mode;
    if (key === ratioKey) { $$('input[name="bc-ratio"]').forEach(function (x) { x.checked = Number(x.value) === st.ratio; }); updateEdge(); return; }
    ratioKey = key;
    var h = '<fieldset class="bc-field bc-field--ratio" aria-describedby="bc-ratio-intro bc-ratio-help"><legend class="bc-field__legend"><span class="bc-field__num" aria-hidden="true">4</span>' + esc(S.ratioLegend) + '</legend>';
    h += '<p class="bc-field__intro" id="bc-ratio-intro">' + esc(S.ratioIntro) + '</p><div class="bc-ratios">';
    list.forEach(function (r) {
      var edge = (bl.edgeNotes || []).some(function (n) { return n.ratio === r; });
      h += '<label class="bc-ratio"><input type="radio" name="bc-ratio" value="' + r + '"' + (st.ratio === r ? ' checked' : '') + (edge ? ' aria-describedby="bc-edge"' : '') + '>' +
        '<span class="bc-ratio__box"><span class="bc-ratio__num" aria-hidden="true">1:' + r + '</span><span class="visually-hidden">' + esc((S.ratioSr || {})[r] || ('1 to ' + r)) + '</span>' +
        '<span class="bc-ratio__word" aria-hidden="true">' + esc((S.ratioWords || {})[r] || '') + '</span></span></label>';
    });
    h += '</div>';
    var note = narrowNote(list);
    if (note) h += '<p class="bc-field__note" data-bc-narrow>' + esc(note) + '</p>';
    h += '<p class="bc-field__help" id="bc-ratio-help">' + esc(S.ratioHelp) + (bl.rangeNote && bl.rangeNote.status === 'approved' ? ' ' + esc(bl.rangeNote.text) : '') + '</p>';
    h += '<p class="bc-edge" id="bc-edge" data-bc-edge hidden></p></fieldset>';
    var hadFocus = document.activeElement && document.activeElement.name === 'bc-ratio';
    box.innerHTML = h;
    if (hadFocus) { var cur = $('input[name="bc-ratio"]:checked'); if (cur) cur.focus(); }
    updateEdge();
  }
  function narrowNote(list) {
    var all = RATIO.allowed;
    if (!st.allowed || list.length >= all.length) return '';
    if (list.length === 1 && S.narrowOne) return fmt(S.narrowOne, { min: list[0] });
    var strongCut = list[0] > all[0], lightCut = list[list.length - 1] < all[all.length - 1];
    var k = strongCut && lightCut ? 'narrowBoth' : st.mode === 'coffee' ? (strongCut ? 'narrowCoffeeStrong' : 'narrowCoffeeLight') : (strongCut ? 'narrowStrong' : 'narrowLight');
    return fmt(S[k], { min: list[0], max: list[list.length - 1] });
  }
  function updateEdge() {
    var el = $('[data-bc-edge]'); if (!el || !cfg) return;
    var e = blurbFor(st.method, st.ratio).edge;
    el.textContent = e ? e.text : ''; el.hidden = !e;
  }
  function clearResult() { if (timerInterrupted()) { tm.noteDue = true; window.setTimeout(function () { if (tm.noteDue) { tm.noteDue = false; say(T.resetNote); } }, 120); } if (tm.io) { tm.io.disconnect(); tm.io = null; } st.shown = false; resultEl.innerHTML = ''; resultEl.hidden = true; resultEl.classList.remove('bc-result--paused'); placeholder.hidden = false; }
  function pauseResult() {
    if (!st.shown) return;
    resultEl.classList.add('bc-result--paused');
    var cb = $('[data-bc-copy]'); if (cb) cb.disabled = true;
    if (timerInterrupted()) tm.pendingNote = true; $$('[data-bc-timer-go], [data-bc-timer-reset]').forEach(function (x) { x.disabled = true; });
  }
  function ratioHtml(r) { return '<span aria-hidden="true">1:' + r + '</span><span class="visually-hidden">1 to ' + r + '</span>'; }
  function timeText(p) { return fmt(p.timing.kind === 'steep' ? S.timeSteep : S.timeTotal, { min: E.mmss(p.timing.minSeconds), max: E.mmss(p.timing.maxSeconds) }); }
  function coffeeLine() {
    var c = st.coffee[st.method];
    if (!c) return '';
    return '<p class="bc-result__coffee">' + esc(fmt(S.brewingWith, { coffee: c.title })) + '. <a href="' + esc(c.url) + '" data-bc-coffee-link="result" data-method="' + esc(st.method) + '" data-product-id="' + esc(c.productId) + '">' + esc(S.shopCoffee) + '</a></p>';
  }
  function linkParams() {
    var o = { method: st.method, recipe: null, water: null, coffee: null, ratio: String(st.ratio) };
    o[st.mode] = g(st.mode === 'coffee' ? st.calc.coffee : st.calc.water);
    return o;
  }
  function renderResult(focus) {
    var c = st.calc, p = c.profile, r = st.ratio, m = M[p.methodId], b = B[p.brewerId], bl = blurbFor(p.methodId, r), def = defaultRatio();
    var h = '<h2 class="bc-result__heading" tabindex="-1" data-bc-result-heading>' + esc(fmt(S.resultHeading, { method: m.label })) + '</h2>';
    h += '<p class="bc-result__meta">' + esc(nb(fmt(S.resultMeta, { brewer: b.label, water: c.water }))) + '</p>';
    h += '<dl class="bc-facts">' +
      '<div class="bc-fact"><dt>' + esc(S.factCoffee) + '</dt><dd>' + c.coffee + '&nbsp;g</dd></div>' +
      '<div class="bc-fact"><dt>' + esc(S.factWater) + '</dt><dd>' + c.water + '&nbsp;g</dd></div>' +
      '<div class="bc-fact"><dt>' + esc(S.factRatio) + '</dt><dd>' + ratioHtml(r) + (r === def ? ' <span class="bc-badge">' + esc(S.recommended) + '</span>' : '') + '</dd></div>' +
      '<div class="bc-fact"><dt>' + esc(S.factTemp) + '</dt><dd>' + c.tempC + '°C (' + c.tempF + '°F)</dd></div>' +
      '<div class="bc-fact bc-fact--wide"><dt>' + esc(S.factTime) + '</dt><dd>' + esc(timeText(p)) + '</dd></div>' +
      '<div class="bc-fact bc-fact--wide"><dt>' + esc(S.factGrind) + '</dt><dd>' + esc(p.grindLabel) + '</dd></div></dl>';
    h += '<div class="bc-result__notes"><p class="bc-result__summary">' + esc((S.summary || {})[r] || '') + '</p>';
    if (bl.blurb) h += '<p>' + esc(bl.blurb.text) + '</p>';
    if (bl.edge) h += '<p class="bc-result__edge">' + esc(bl.edge.text) + '</p>';
    if (bl.taste) h += '<p class="bc-result__taste">' + esc(bl.taste) + '</p>';
    h += '</div><div data-bc-result-coffee>' + coffeeLine() + '</div>';
    h += '<h3 class="bc-result__steps-h">' + esc(S.stepsHeading) + '</h3><ol class="bc-steps">';
    c.steps.forEach(function (s) {
      h += '<li>' + esc(nb(s.text)) + (s.type === 'pour' && s.add !== s.cum ? ' <span class="bc-steps__add">(' + esc(nb(fmt(S.pourAdd, { g: s.add }))) + ')</span>' : '') + '</li>';
    });
    h += '</ol>' + timerHtml(c) + '<div class="bc-result__actions">' +
      '<button type="button" class="btn btn-solid bc-btn" data-bc-copy>' + esc(S.copy) + '</button>' +
      ($('#bc-video-' + p.methodId) ? '<a class="btn btn-ghost bc-btn" href="#bc-video-' + esc(p.methodId) + '" data-bc-video-link>' + esc(S.videoLink) + '</a>' : '') +
      '<a class="bc-result__guide" href="' + esc(m.guideUrl) + '" data-bc-guide-link data-method="' + esc(p.methodId) + '" data-link-type="result">' + esc(fmt(S.guideLink, { method: m.label })) + '</a></div>' +
      '<div class="bc-copy-fallback" data-bc-copy-fallback hidden><label for="bc-copy-text">' + esc(S.copyFallbackLabel) + '</label><textarea id="bc-copy-text" rows="10" readonly></textarea></div>';
    var wasTiming = timerInterrupted() || !!tm.pendingNote; tm.pendingNote = false;
    resultEl.innerHTML = h; resultEl.hidden = false; resultEl.classList.remove('bc-result--paused'); placeholder.hidden = true; st.shown = true;
    timerReset(wasTiming ? T.resetNote : null); if (wasTiming) window.setTimeout(function () { say(T.resetNote); }, 400);
    setParams(linkParams());
    var data = recipeData({ coffee_g: Number(c.coffee), blurb_id: bl.blurb ? bl.blurb.id : null, edge_note_id: bl.edge ? bl.edge.id : null, is_custom_amount: c.water !== p.waterG });
    var key = 'gen:' + p.id + ':' + r + ':' + st.mode + ':' + st.amount;
    window.clearTimeout(genTimer);
    if (focus) once(key, 'brew_recipe_generated', data); else genTimer = window.setTimeout(function () { once(key, 'brew_recipe_generated', data); }, 1200);
    if (focus) { var hd = $('[data-bc-result-heading]'); if (hd) hd.focus(); }
  }
  /* Brew timer UI: one big prompt card, a heads up 5 seconds before each action, optional sound or vibration (off by default).
     Timing is timestamp based (performance.now minus the start time), never tick counting, so a throttled or background tab
     catches up on the next tick or when it becomes visible. The clock is role timer (not announced every second); prompts,
     heads ups and notes go to the polite status region. While running, a screen wake lock is held where supported, and a
     compact fixed bar (clock, short prompt, Pause) shows when the card is scrolled out of view. */
  var T = S.timer || {}, tm = { plan: null, run: false, active: false, done: false, base: 0, t0: 0, cur: -1, heads: {}, iv: null, audio: null, wake: null, cardSeen: true, io: null, bar: null };
  function now() { return window.performance && performance.now ? performance.now() : Date.now(); }
  var nb = E.nb;
  function promptText(cue) { return cue ? nb(fmt((T.prompts || {})[cue.key], { g: cue.g, n: cue.n })) : ''; }
  function shortText(cue) { return cue ? nb(fmt((T.short || {})[cue.key] || (T.prompts || {})[cue.key], { g: cue.g, n: cue.n })) : ''; }
  function timerHtml(c) {
    var plan = E.timerPlan(c.profile, c);
    if (!plan || !T.prompts) return '';
    return '<section class="bc-timer" data-bc-timer aria-labelledby="bc-timer-h"><h3 class="bc-result__steps-h" id="bc-timer-h">' + esc(T.heading) + '</h3>' +
      '<p class="bc-timer__intro">' + esc(T.intro) + '</p>' +
      '<div class="bc-timer__card" data-bc-timer-card data-state="ready"><p class="bc-timer__clock"><span class="bc-timer__clock-label">' + esc(T.clockLabel) + '</span> <span role="timer" aria-live="off" data-bc-timer-clock>0:00</span></p>' +
      '<p class="bc-timer__prompt" data-bc-timer-prompt></p><p class="bc-timer__next" data-bc-timer-next></p></div>' +
      '<div class="bc-timer__controls"><button type="button" class="btn btn-solid bc-btn" data-bc-timer-go>' + esc(T.startLabel) + '</button>' +
      '<button type="button" class="btn btn-ghost bc-btn" data-bc-timer-reset hidden>' + esc(T.reset) + '</button></div>' +
      '<label class="bc-timer__cue"><input type="checkbox" data-bc-timer-cue' + (tm.cueOn ? ' checked' : '') + '><span>' + esc(T.cueToggle) + '</span></label></section>';
  }
  function elapsed() { return tm.base + (tm.run ? (now() - tm.t0) / 1000 : 0); }
  /* Wake lock: held while running, released on pause, start over, recipe change and the end. Fails quietly. */
  function wakeOn() {
    try {
      if (!tm.run || tm.wake || !navigator.wakeLock || document.visibilityState !== 'visible') return;
      navigator.wakeLock.request('screen').then(function (l) {
        if (!tm.run) { l.release().catch(function () {}); return; }
        tm.wake = l; l.addEventListener('release', function () { if (tm.wake === l) tm.wake = null; });
      }).catch(function () {});
    } catch (e) {}
  }
  function wakeOff() { try { if (tm.wake) { var l = tm.wake; tm.wake = null; l.release().catch(function () {}); } } catch (e) {} }
  /* Sticky bar: fixed overlay (no layout shift), lifted above the cookie banner when that is showing. */
  function bar() {
    if (tm.bar) return tm.bar;
    var b = document.createElement('div');
    b.className = 'bc-sticky'; b.hidden = true; b.setAttribute('role', 'region'); b.setAttribute('aria-label', T.barLabel || T.heading || 'Timer');
    b.innerHTML = '<span class="bc-sticky__clock" aria-hidden="true" data-bc-bar-clock>0:00</span><span class="bc-sticky__prompt" data-bc-bar-prompt></span>' +
      '<button type="button" class="bc-sticky__btn" data-bc-bar-go>' + esc(T.pause) + '</button>';
    b.querySelector('[data-bc-bar-go]').addEventListener('click', function () { timerGo(); });
    document.body.appendChild(b); tm.bar = b; return b;
  }
  function bannerOffset() {
    try {
      var c = document.getElementById('shopify-pc__banner'); if (!c) return 0;
      var cs = getComputedStyle(c), r = c.getBoundingClientRect();
      if (cs.display === 'none' || cs.visibility === 'hidden' || !r.height || r.top >= window.innerHeight) return 0;
      return Math.max(0, Math.round(window.innerHeight - r.top));
    } catch (e) { return 0; }
  }
  function updateBar() {
    var show = tm.active && !tm.done && !tm.cardSeen && !!tm.plan;
    if (!show) { if (tm.bar) tm.bar.hidden = true; return; }
    var b = bar();
    b.querySelector('[data-bc-bar-clock]').textContent = E.mmss(Math.floor(elapsed()));
    b.querySelector('[data-bc-bar-prompt]').textContent = shortText(tm.plan[tm.cur]);
    b.querySelector('[data-bc-bar-go]').textContent = tm.run ? T.pause : T.resume;
    b.style.bottom = (bannerOffset() + (window.matchMedia && window.matchMedia('(min-width: 990px)').matches ? 24 : 0)) + 'px';
    b.hidden = false;
  }
  function watchCard() {
    if (tm.io) { tm.io.disconnect(); tm.io = null; }
    tm.cardSeen = true;
    var card = $('[data-bc-timer-card]');
    if (!card || !('IntersectionObserver' in window)) return;
    tm.io = new IntersectionObserver(function (es) { tm.cardSeen = es[es.length - 1].isIntersecting; updateBar(); }, { threshold: 0 });
    tm.io.observe(card);
  }
  function timerReset(note) {
    timerStop(); tm.base = 0; tm.cur = 0; tm.heads = {}; tm.active = false; tm.done = false;
    tm.plan = st.calc ? E.timerPlan(st.calc.profile, st.calc) : null;
    updateBar();
    var go = $('[data-bc-timer-go]'); if (!go || !tm.plan) return;
    go.textContent = T.startLabel; go.disabled = false; go.hidden = false;
    var rs = $('[data-bc-timer-reset]'); rs.hidden = true; rs.disabled = false;
    $('[data-bc-timer-card]').setAttribute('data-state', 'ready');
    $('[data-bc-timer-clock]').textContent = '0:00';
    $('[data-bc-timer-prompt]').textContent = promptText(tm.plan[0]);
    showNext(1, note);
    watchCard();
  }
  function showNext(i, heads) {
    var el = $('[data-bc-timer-next]'), cue = tm.plan[i];
    if (!el) return;
    el.classList.toggle('bc-timer__next--soon', !!heads);
    if (heads) { el.textContent = heads; return; }
    el.textContent = cue ? fmt(T.next, { time: E.mmss(cue.at) }) + ' ' + promptText(cue) : '';
  }
  function cueOut() {
    if (!tm.cueOn) return;
    try { if (navigator.vibrate) navigator.vibrate(200); } catch (e) {}
    try {
      var a = tm.audio; if (!a) return;
      var o = a.createOscillator(), gn = a.createGain();
      o.frequency.value = 880; gn.gain.setValueAtTime(0.0001, a.currentTime); gn.gain.exponentialRampToValueAtTime(0.2, a.currentTime + 0.02); gn.gain.exponentialRampToValueAtTime(0.0001, a.currentTime + 0.25);
      o.connect(gn); gn.connect(a.destination); o.start(); o.stop(a.currentTime + 0.3);
    } catch (e) {}
  }
  function tick() {
    if (!tm.plan) return;
    var t = elapsed(), p = tm.plan, i = tm.cur, clock = $('[data-bc-timer-clock]');
    if (!clock) { timerStop(); return; }
    clock.textContent = E.mmss(Math.floor(t));
    while (p[i + 1] && t >= p[i + 1].at) i++;
    if (i !== tm.cur) {
      tm.cur = i;
      $('[data-bc-timer-prompt]').textContent = promptText(p[i]);
      showNext(i + 1);
      say(promptText(p[i])); cueOut();
      if (!p[i + 1]) {
        timerStop(); tm.done = true; $('[data-bc-timer-card]').setAttribute('data-state', 'done');
        var go = $('[data-bc-timer-go]'); go.hidden = true; updateBar(); return;
      }
    }
    var nx = p[i + 1];
    if (nx && nx.headsUp && !tm.heads[i + 1] && t >= nx.at - E.HEADS_UP_S && t < nx.at) {
      tm.heads[i + 1] = 1;
      var msg = fmt((T.headsUp || {})[nx.headsUp], { s: E.HEADS_UP_S });
      showNext(i + 1, msg); say(msg);
    }
    updateBar();
  }
  function timerStop() { if (tm.iv) { window.clearInterval(tm.iv); tm.iv = null; } if (tm.run) { tm.base = elapsed(); tm.run = false; } wakeOff(); }
  /* Called before the result changes or clears: a running or paused timer is reset with a short note. */
  function timerInterrupted() { var was = tm.active && !tm.done; timerStop(); tm.active = false; updateBar(); return was; }
  function timerGo() {
    var go = $('[data-bc-timer-go]'); if (!go || !tm.plan || tm.done) return;
    if (tm.run) { timerStop(); go.textContent = T.resume; $('[data-bc-timer-card]').setAttribute('data-state', 'paused'); say(T.paused); updateBar(); return; }
    if (tm.cueOn && !tm.audio) { try { var AC = window.AudioContext || window.webkitAudioContext; if (AC) tm.audio = new AC(); } catch (e) {} }
    tm.run = true; tm.active = true; tm.t0 = now(); go.textContent = T.pause;
    $('[data-bc-timer-reset]').hidden = false; $('[data-bc-timer-card]').setAttribute('data-state', 'running');
    wakeOn(); tick(); tm.iv = window.setInterval(tick, 250);
  }
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && tm.run) { wakeOn(); tick(); }
  });
  window.addEventListener('resize', function () { if (tm.bar && !tm.bar.hidden) updateBar(); });
  function copyText() {
    var c = st.calc, p = c.profile, r = st.ratio, m = M[p.methodId], b = B[p.brewerId], co = st.coffee[st.method], lp = linkParams();
    var L = [S.copyTitle, m.label + ', ' + b.label, S.factCoffee + ': ' + c.coffee + ' g', S.factWater + ': ' + c.water + ' g',
      S.factRatio + ': 1:' + r + (r === defaultRatio() ? ' (' + S.recommended + ')' : ''), S.factGrind + ': ' + p.grindLabel,
      S.factTemp + ': ' + c.tempC + '°C (' + c.tempF + '°F)', S.factTime + ': ' + timeText(p)];
    if (co) L.push(fmt(S.brewingWith, { coffee: co.title }) + ': ' + window.location.origin + co.url);
    L.push('', S.stepsHeading + ':');
    c.steps.forEach(function (s, i) { L.push((i + 1) + '. ' + s.text); });
    L.push('', S.copyLink + ': ' + window.location.origin + (root.getAttribute('data-canonical-path') || window.location.pathname) +
      '?method=' + encodeURIComponent(st.method) + '&' + st.mode + '=' + encodeURIComponent(lp[st.mode]) + '&ratio=' + r);
    return L.join('\n');
  }
  function doCopy() {
    if (!st.calc) return;
    var text = copyText(), fb = $('[data-bc-copy-fallback]');
    var fallback = function () {
      if (!fb) return;
      fb.hidden = false; var ta = fb.querySelector('textarea'); ta.value = text; ta.focus(); ta.select();
      say(S.copyFallback); track('brew_recipe_copy_fallback', recipeData());
    };
    if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(text).then(function () { say(S.copied); track('brew_recipe_copied', recipeData()); }, fallback);
    else fallback();
  }
  /* After any amount, mode or ratio change: update the amount line, ratio picker and (if shown) the result in place. */
  function refresh(showProblem, announce) {
    var prevRatio = st.ratio;
    evaluate(); updateAmountUi(showProblem); renderRatio();
    var n = $('[data-bc-narrow]');
    if (st.calc && prevRatio !== st.ratio && n) say(n.textContent);
    if (st.shown) {
      if (st.calc) { renderResult(false); if (announce) say(announce()); }
      else if (showProblem) pauseResult();
    }
    if (showProblem && !st.calc && st.problem && st.problem !== 'empty') say(problemText());
  }
  function amountAnnounce() { return fmt(S.updatedAmount, { amount: g(st.amount), unit: st.mode === 'coffee' ? S.unitCoffee : S.unitWater }); }
  function setAmount(text, source) {
    st.text = text; st.source = source;
    var inp = $('[data-bc-amount]'); if (inp && inp.value !== text) inp.value = text;
    refresh(true, amountAnnounce);
  }
  function setMode(mode) {
    if (mode === st.mode) return;
    var c = st.calc, from = st.mode, before = st.text;
    st.mode = mode;
    /* Switching back without edits restores what was typed, so 500 g water to 31.3 g coffee and back gives 500 again. */
    if (st.conv && st.conv.from === mode && st.conv.to === st.text) { st.text = st.conv.text; st.conv = null; }
    else if (c) { st.text = mode === 'coffee' ? g(c.coffee) : String(c.water); st.conv = { from: from, text: before, to: st.text }; }
    var inp = $('[data-bc-amount]'); if (inp) inp.value = st.text;
    refresh(st.problem && st.problem !== 'empty', null);
    if (st.shown && st.calc) say(amountAnnounce());
  }
  function chooseMethod(mid, quiet) {
    st.method = mid; st.brewer = null; st.text = ''; st.amount = null; st.calc = null; st.allowed = null; st.problem = 'empty';
    if (RATIO.allowed.indexOf(st.ratio) < 0) st.ratio = defaultRatio();
    $$('input[name="bc-method"]').forEach(function (i) { i.checked = i.value === mid; });
    setActive(mid); clearResult(); renderControls();
    if (!started) { started = true; track('brew_calculator_started', { method_id: mid }); }
    once('mv:' + mid, 'brew_method_view', { method_id: mid });
    if (!quiet) { setParams({ method: mid, recipe: null, water: null, coffee: null, ratio: null }); var mc = fmt(S.methodChosen, { method: M[mid].label }); if (tm.noteDue) { tm.noteDue = false; mc = T.resetNote + ' ' + mc; } say(mc); }
  }

  function bind() {
    form.addEventListener('change', function (e) {
      var t = e.target; if (!t) return;
      if (t.hasAttribute && t.hasAttribute('data-bc-amount')) { window.clearTimeout(typeTimer); st.text = t.value; if (st.evalKey !== st.mode + '|' + st.text) refresh(true, amountAnnounce); return; }
      if (t.type !== 'radio') return;
      notice('');
      if (t.name === 'bc-method') chooseMethod(t.value);
      else if (t.name === 'bc-brewer') { st.brewer = t.value; clearResult(); renderBatch(); refresh(false, null); }
      else if (t.name === 'bc-mode') setMode(t.value);
      else if (t.name === 'bc-ratio') {
        st.ratio = parseInt(t.value, 10);
        evaluate(); updateAmountUi(false); renderRatio();
        if (st.shown && st.calc) { renderResult(false); say(fmt(S.updated, { ratio: st.ratio })); }
      }
      var err = $('[data-bc-form-error]'); if (err) err.hidden = true;
    });
    form.addEventListener('input', function (e) {
      var t = e.target; if (!t || !t.hasAttribute || !t.hasAttribute('data-bc-amount')) return;
      st.text = t.value; st.source = 'typed';
      var err = $('[data-bc-form-error]'); if (err) err.hidden = true;
      window.clearTimeout(typeTimer);
      typeTimer = window.setTimeout(function () { refresh(true, amountAnnounce); }, 400);
    });
    form.addEventListener('submit', function (e) {
      e.preventDefault(); window.clearTimeout(typeTimer);
      var err = $('[data-bc-form-error]');
      if (!st.method) { notice(S.needMethod); var f = $('input[name="bc-method"]'); if (f) f.focus(); return; }
      var inp = $('[data-bc-amount]'); if (inp) st.text = inp.value;
      refresh(true, null);
      if (!st.calc) {
        if (st.problem === 'empty' && err) { err.textContent = S.needBatch; err.hidden = false; say(S.needBatch); }
        if (inp) inp.focus();
        return;
      }
      renderResult(true);
    });
    root.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target : null; if (!t) return;
      var el;
      if ((el = t.closest('[data-bc-quick-value]'))) { setAmount(el.getAttribute('data-bc-quick-value'), 'quick_pick'); return; }
      if ((el = t.closest('[data-bc-use]'))) { setAmount(el.getAttribute('data-bc-use'), 'nearest'); var i = $('[data-bc-amount]'); if (i) i.focus(); return; }
      if ((el = t.closest('[data-bc-timer-go]'))) { timerGo(); return; }
      if ((el = t.closest('[data-bc-timer-reset]'))) { timerReset(); var g2 = $('[data-bc-timer-go]'); g2.hidden = false; g2.focus(); say(promptText(tm.plan && tm.plan[0])); return; }
      if ((el = t.closest('[data-bc-copy]'))) { doCopy(); return; }
      if ((el = t.closest('[data-bc-video]'))) { playVideo(el); return; }
      if ((el = t.closest('[data-bc-gear]'))) { track('brew_gear_click', { method_id: el.getAttribute('data-method'), asin: el.getAttribute('data-bc-gear') }); return; }
      if ((el = t.closest('[data-bc-coffee-link]'))) { track('brew_coffee_clicked', recipeData({ method_id: el.getAttribute('data-method') || st.method, product_id: el.getAttribute('data-product-id') || null, link_type: el.getAttribute('data-bc-coffee-link') })); return; }
      if ((el = t.closest('[data-bc-guide-link]'))) { track('brew_guide_clicked', recipeData({ method_id: el.getAttribute('data-method') || st.method, link_type: el.getAttribute('data-link-type') || 'guide' })); return; }
      if ((el = t.closest('[data-bc-retry]'))) { start(); }
    });
    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.hasAttribute && t.hasAttribute('data-bc-timer-cue')) { tm.cueOn = t.checked; if (t.checked && !tm.audio) { try { var AC = window.AudioContext || window.webkitAudioContext; if (AC) tm.audio = new AC(); } catch (er) {} } if (t.checked) cueOut(); return; }
      if (!t || !t.hasAttribute || !t.hasAttribute('data-bc-coffee')) return;
      var mid = t.getAttribute('data-method');
      st.coffee[mid] = { title: t.getAttribute('data-title'), url: t.getAttribute('data-url'), productId: t.getAttribute('data-product-id'), variantId: t.getAttribute('data-variant-id') };
      say(fmt(S.coffeeChosen, { coffee: st.coffee[mid].title }));
      var slot = $('[data-bc-result-coffee]'); if (slot && st.shown && st.method === mid) slot.innerHTML = coffeeLine();
      track('brew_coffee_selected', { method_id: mid, product_id: st.coffee[mid].productId, variant_id: st.coffee[mid].variantId, recipe_id: st.calc ? st.calc.profile.id : null, ratio: st.ratio });
    });
  }
  function playVideo(btn) {
    var id = btn.getAttribute('data-bc-video'); if (!/^[A-Za-z0-9_-]{6,20}$/.test(id)) return;
    var f = document.createElement('iframe');
    f.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&playsinline=1';
    f.title = btn.getAttribute('data-bc-video-title') || 'Video';
    f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    f.setAttribute('allowfullscreen', ''); f.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    btn.parentNode.replaceChild(f, btn); f.focus();
    track('brew_video_play', { method_id: btn.getAttribute('data-method'), video_id: id });
  }
  /* Links: ?recipe=<approved id>&ratio=N (old) or ?method=<id>&water=N|coffee=N&ratio=N (new).
     Unknown or retired recipe ids get unknownRecipe; an amount I can't read (or both water and coffee) gets badLinkAmount. */
  function applyLink() {
    var rid = query.get('recipe'), rq = query.get('ratio'), mq = query.get('method'), wq = query.get('water'), cq = query.get('coffee');
    var mid = null, mode = 'water', text = null;
    if (rid) {
      var p = P.filter(function (x) { return x.id === rid; })[0];
      if (!p) { setActive(null); notice(S.unknownRecipe); setParams({ recipe: null, ratio: null, method: null, water: null, coffee: null }); return; }
      mid = p.methodId; text = String(p.waterG);
    } else if (mq) {
      mid = ALIAS[mq.toLowerCase()];
      if (!mid || !brewersFor(mid).length) { setActive(null); notice(S.unknownMethod); setParams({ method: null, water: null, coffee: null, ratio: null }); return; }
      if (wq != null && cq != null) { chooseMethod(mid, true); notice(S.badLinkAmount || S.unknownRecipe); setParams({ method: mid, water: null, coffee: null, ratio: null }); return; }
      if (wq != null) text = wq; else if (cq != null) { text = cq; mode = 'coffee'; }
    } else return;
    chooseMethod(mid, true);
    if (mq && mq !== mid) setParams({ method: mid });
    if (text == null) return;
    st.mode = mode; st.text = text; renderBatch();
    evaluate();
    if (st.problem === 'nan') { st.text = ''; renderBatch(); notice(S.badLinkAmount || S.unknownRecipe); setParams({ water: null, coffee: null, ratio: null }); refresh(false, null); return; }
    if (!st.calc) { refresh(true, null); setParams({ method: mid, recipe: null, ratio: null }); return; }
    if (rq != null) {
      var r = parseInt(rq, 10);
      if (String(r) === rq && st.allowed.indexOf(r) > -1) st.ratio = r;
      else {
        var def = defaultRatio(), msg;
        if (st.allowed.indexOf(def) > -1) { st.ratio = def; msg = S.invalidRatio; } else msg = fmt(S.invalidRatioNarrow, { ratio: st.ratio });
        var oor = cfg.blurbs && cfg.blurbs.outOfRangeNote;
        if (oor && oor.status === 'approved' && !(r >= RATIO.allowed[0] && r <= RATIO.allowed[RATIO.allowed.length - 1])) msg += ' ' + oor.text;
        ratioKey = ''; refresh(false, null); notice(msg); setParams({ method: mid, recipe: null, ratio: null });
        return;
      }
    }
    ratioKey = ''; refresh(false, null); renderResult(false);
  }
  function ready() {
    $$('input[name="bc-method"]').forEach(function (i) {
      var ok = brewersFor(i.value).length > 0; i.disabled = !ok;
      var lab = i.closest('[data-bc-method-option]'); if (lab) lab.hidden = !ok;
    });
    root.setAttribute('data-state', 'ready'); errorEl.hidden = true;
    if (!st.ratio) st.ratio = RATIO.def;
    track('brew_calculator_viewed', {});
    applyLink();
    if (!st.method) { loadingEl.textContent = S.pickMethod; loadingEl.hidden = false; say(S.ready); }
  }
  function fail(err) {
    root.setAttribute('data-state', 'error'); loadingEl.hidden = true; errorEl.hidden = false;
    if (window.console && console.warn) console.warn('Brew calculator data unavailable:', err && err.message);
  }
  function start() {
    root.setAttribute('data-state', 'loading'); errorEl.hidden = true;
    if (!loadingEl.parentNode) { controls.innerHTML = ''; controls.appendChild(loadingEl); }
    loadingEl.textContent = S.loading; loadingEl.hidden = false;
    fetch(root.getAttribute('data-recipes-url'), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (c) { load(c); ready(); })
      .catch(fail);
  }
  bind();
  start();
})();
